import { Inject, Injectable } from '@angular/core';
import { Ability, AbilityBuilder } from '@casl/ability';
import { KeycloakService } from 'keycloak-angular';
import {
  combineLatest, Observable, of, throwError
} from 'rxjs';
import {
  catchError,
  concatMap, mergeMap, take, tap
} from 'rxjs/operators';

import { dealerRightsDefinitions } from 'app/constants/permissions/dealer-permissions.data';
import { manufacturerRightsDefinitions } from 'app/constants/permissions/manufacturer-permissions.data';
import {
  Account, AccountRoles, Branch, BranchRoleMapping
} from 'graphql/models/generated';
import {
  AccountRole, AccountTypeENUM
} from 'models/account.model';
import {
  Subject, Action, AccountRightsDefs, RightsType, AccountRoleRightsDefs
} from 'models/roles.model';
import { AccountService } from 'shared/services/account.service';
import { StoreService } from 'shared/services/store.service';

import { SettingsService } from './settings.service';
import { APP_ABILITY, AppAbility } from 'app/utils/ability.factory';

@Injectable(
  {
    providedIn: 'root'
  }
)
export class SessionService {
  private isDealer!: boolean;
  private currentAccount: any;

  private subjects: Subject[] = Object.values(Subject);

  constructor(
    private keycloakService: KeycloakService,
    @Inject(APP_ABILITY) private ability: AppAbility,
    private storeService: StoreService,
    private accountService: AccountService,
    private settingsService: SettingsService
  ) { }

  logout(): Observable<any> {
    return this.accountService.logout()
      .pipe(
        tap(() => {
          this.keycloakService.logout(window.location.origin);
          this.ability.update([]);

          // clear the local storage manually,
          // because Apollo's "clearStore" and "resetStore" are not working properly.
          window.localStorage.clear();
        })
      );
  }

  initCurrentAccountAndBranch(username: string): Observable<boolean> {
    return combineLatest([
      this.accountService.getAccountProfile(undefined, username),
      this.storeService.getCurrentAccountBranch(),
      this.settingsService.getBranches(),
    ]).pipe(
      mergeMap(([acc, currentBranch, branches]: [Account, Branch, Branch[]]) => {
        if (!acc || (acc && !acc.accountType)) {
          return throwError(() => new Error('Account not found'));
        }
        this.storeService.setAccount(acc);

        let branchId = '';
        if (acc.accountType === AccountTypeENUM.MANUFACTURER) {
          branchId = acc.manufacturer;
        } else {
          this.storeService.getBranches().subscribe((res) => {
            if (!res || res.length === 0) {
              this.storeService.setBranches(branches);
            }
          });

          if (currentBranch?.id) {
            branchId = currentBranch.id;
          } else {
            const branchRoles = acc.accountRoles?.branchRoles || [];
            const mainBranchId = branchRoles[0]?.branchId

            const match = branches.find(b => b.id === mainBranchId);
            if (!match?.missingInSource) {
              branchId = mainBranchId;
            } else {
              const firstValidBranch = branches.find(b => !b.missingInSource);
              if (!firstValidBranch) {
                console.error("NO VALID BRANCH FOUND");
              }
              branchId = firstValidBranch?.id ?? '';
            }
          }

          if (!branchId) {
            return throwError(() => new Error('Account branch not found'));
          }
        }

        return this.settingsService.getBranch(branchId);
      }),
      tap((branch: Branch) => this.storeService.setCurrentAccountBranch(branch)),
      concatMap(() => of(true))
    );
  }

  setupRole(username: string): Observable<[boolean, Account]> {
    return this.initCurrentAccountAndBranch(username)
      .pipe(
        catchError(() => { 
          return this.logout();
        }),
        concatMap(() => combineLatest([
          this.storeService.isCurrentAccountDealer(),
          this.storeService.getAccount()
        ])),
        tap(([isDealer, account]) => {
          this.isDealer = isDealer;
          this.currentAccount = account;
          this.updateAbility(account);
        }),
        take(1)
      );
  }

  private updateAbility(account: Account): void {
    const { can, rules } = new AbilityBuilder<Ability>(Ability);
    const accountRoles = account.accountRoles as AccountRoles;
    const orgRole = accountRoles.organizationRole as AccountRole;

    // Set Org Permissions
    if (orgRole) {
      const orgRights: AccountRoleRightsDefs = this.getRightsByType(RightsType.Organization);
      this.setOrgAbilities(orgRights, orgRole, can);
    }

    // Set Branch Permissions (applied only for dealer)
    if (this.isDealer) {
      const branchRights: AccountRoleRightsDefs = this.getRightsByType(RightsType.Branch);
      this.setBranchAbilities(branchRights, accountRoles.branchRoles, can);
    }

    this.ability.update(rules);
  }

  private getRightsByType(type: RightsType): AccountRoleRightsDefs {
    const rights: AccountRightsDefs = this.getAccountRightsDefs();

    return this.subjects
      .filter((subject: Subject) => Boolean(rights[subject][type]))
      .reduce((acc, subject: Subject) => ({ ...acc, [subject]: rights[subject][type] }), {} as AccountRoleRightsDefs);
  }

  private getAccountRightsDefs(): AccountRightsDefs {
    const accRightsDefs: AccountRightsDefs = this.isDealer
      ? dealerRightsDefinitions : manufacturerRightsDefinitions;

    return this.subjects.reduce(
      (acc: AccountRightsDefs, subject: Subject) => (
        { ...acc, [subject]: accRightsDefs[subject] }
      ),
      {} as AccountRightsDefs
    );
  }

  private setOrgAbilities(orgRights: AccountRoleRightsDefs, role: AccountRole, can: any): void {
    Object.keys(orgRights).forEach((subject) => {
      const actions = orgRights[subject as Subject][role];

      if (subject === "Account") {
          if (this.currentAccount.accountRoles.organizationRole === 'SUPERADMIN') {
            can(Action.Update, Subject.Account);
            can(Action.Read, Subject.Account);
          } else if (this.currentAccount.accountRoles.organizationRole === 'ADMIN') {
            can(Action.Update, Subject.Account, { role: { $in: ['ADMIN' ,'USER'] } });
            can(Action.Read, Subject.Account);
          } else {
            can(Action.Update, Subject.Account, { role: { $in: ['USER'] } });
            can(Action.Read, Subject.Account);
          }
      } else {
        actions?.forEach((action: any) => {
          can(action, subject);
        });
      }
    });
  }

  private setBranchAbilities(branchRights: AccountRoleRightsDefs, branchRoles: BranchRoleMapping[], can: any): void {
    branchRoles
      .filter((branchRole: BranchRoleMapping) => branchRole.role)
      .forEach((branchRole: BranchRoleMapping) => {
        Object.keys(branchRights).forEach((subject) => {
          const actions = branchRights[subject as Subject][branchRole.role as AccountRole];

          actions.forEach((action: Action) => {
            can(action, subject, { branch: branchRole.branchId });
          });
        });
      });
  }
}
